<script setup lang="ts">
import type { SaferGamblingQas } from '@content-utils/schemas'
import { computed } from '#imports'

export interface SaferGamblingEntrancePanelProps {
    title: string
    header?: string
    category: string
    backLabel: string
}

const props = defineProps<SaferGamblingEntrancePanelProps>()

const emit = defineEmits<{
    goBack: []
}>()

const { queryByCategory } = useLayeredContent()

// Filter QAs by category (e.g., qas/general/*)
const { data: qas } = useAsyncData(
    `saferGambling:entranceQas:${props.category}`,
    () => queryByCategory<SaferGamblingQas>('safer-gambling/qas', props.category),
    { server: false }
)

const mappedQas = computed(() => {
    if (!qas.value?.length) return []

    return qas.value.map(qa => ({
        id: (qa as any).id as string,
        title: qa.title,
        video_id: qa.video_id,
        body: (qa as any).body,
        ctas: qa.ctas ?? [],
        expandableTextBlocks: qa.expandableTextBlocks ?? [],
    }))
})
</script>

<template>
    <div class="entrance-panel">
        <div class="entrance-panel__header">
            <span v-if="header">{{ header }}</span>
        </div>

        <div class="entrance-panel__content">
            <SaferGamblingQaPanel
                v-for="qa in mappedQas"
                :key="qa.id"
                :title="qa.title"
                :video-id="qa.video_id"
                :body="qa.body"
                :ctas="qa.ctas"
                :expandable-text-blocks="qa.expandableTextBlocks"
                borderless
            />

            <div v-if="!mappedQas.length" class="entrance-panel__empty">
                <UILoader />
            </div>
        </div>

        <div
            class="entrance-panel__return-link"
            @click="emit('goBack')"
        >
            <UIIcon class="entrance-panel__return-link-icon" name="arrow-left" size="8px" />
            <span class="entrance-panel__return-link-text">{{ props.backLabel }}</span>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.entrance-panel {
    padding: 15px;
    background-color: $panel-color;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;

    &__return-link {
        background: none;
        border: none;
        font-weight: $text-bold;
        color: $link-color;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &-text:hover {
            cursor: pointer;
            text-decoration: underline;
        }

        &-icon {
            margin-right: 8px;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 15px;
        justify-content: center;
        align-items: center;
    }

    &__empty {
        display: flex;
        justify-content: center;
        padding: 20px;
    }

    :deep(.qa-panel) {
        width: 100%;

        .qa-panel__header {
            text-align: center;
            border-radius: 8px;
            background-color: $safer-gambling-qa-bg-color;
            justify-content: center;
            padding: 15px;
        }

        .qa-panel__header-icon {
            display: none;
            visibility: hidden;
        }
    }
}
</style>
